/* global window */

import log from 'loglevel';
import loadJS from 'load-js';
import get from 'lodash/get';
import store from 'store/dist/store.modern';

import settings from './settings';

/**
 * Class representing Braze.
 */
class Braze {
  /**
   * Create a Braze instance.
   * @param {Object} params - Braze configuration parameters.
   */
  constructor(parms) {
    this.parms = parms;
    this.currentUserData = {};
    this.lastPushRequestKey = 'MetaphorLastPushRequest';
  }

  /**
   * Check if Braze global script is ready.
   * @returns {Boolean}
   */
  get isBrazeReady() {
    return typeof window.appboy !== 'undefined';
  }

  /**
   * Load Braze global script.
   * @returns {Promise}
   */
  load() {
    const scriptsFound = typeof get(settings, 'braze.scripts') !== 'undefined';
    const apiKeyFound = typeof get(settings, 'braze.apiKey') !== 'undefined';

    if (!scriptsFound || !apiKeyFound) {
      log.debug('Braze settings not found.');
      return Promise.resolve();
    }

    const scripts = get(settings, 'braze.scripts', []);
    const apiKey = get(settings, 'braze.apiKey', '');

    if (scripts.length !== 1 || !apiKey.length) {
      log.debug('Invalid Braze settings.');
      return Promise.resolve();
    }

    return loadJS({
      url: settings.braze.scripts,
      async: true,
    });
  }

  /**
   * Initialize Braze global script.
   */
  init() {
    if (!this.isBrazeReady) {
      log.error('Tried to initialize Braze before the script was ready.');
      return;
    }

    window.appboy.initialize(settings.braze.apiKey, {
      doNotLoadFontAwesome: true,
      enableHtmlInAppMessages: settings.braze.enableHtmlInAppMessages,
      safariWebsitePushId: settings.braze.safariWebsitePushId,
    });

    window.appboy.display.automaticallyShowNewInAppMessages();

    log.debug('Braze initialized.');
  }

  /**
   * Fetches user ID.
   * @returns {Promise}
   */
  fetchCurrentUserData() {
    window.appboy.getUser().getUserId(userId => {
      if (userId) {
        log.debug(`Braze userId = [${userId}]`);
        this.currentUserData.userId = userId;
      }
      return Promise.resolve();
    });
  }

  /**
   * Return user ID.
   * @returns {String}
   */
  get userId() {
    return this.currentUserData.userId;
  }

  /**
   * Open Braze session.
   * @returns {Promise}
   */
  openSession() {
    return new Promise(resolve => {
      window.appboy.openSession(() => {
        log.debug('Braze session opened.');

        const siteId = get(settings, 'site_id', 'vl_nola');
        const eventName = `siteVisit.${siteId}`;
        window.appboy.logCustomEvent(eventName);
        log.debug(`Braze custom event [${eventName}] triggered.`);
        return resolve();
      });
    });
  }

  /**
   * Change user ID.
   * @param {String} userId - SHA-256 hash of user's email.
   * @returns {Promise}
   */
  changeUser(userId) {
    if (!userId) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      window.appboy.changeUser(userId, () => {
        log.debug(`Braze userId changed [${userId}]`);
        return resolve();
      });
    });
  }

  /**
   * Check if push prompt can be triggered.
   * @returns {Boolean}
   */
  get canRequestPushPermission() {
    if (!store.enabled) {
      log.error('Storage not available.');
      return true;
    }

    const lastRequest = store.get(this.lastPushRequestKey);
    if (typeof lastRequest === 'undefined') {
      return true;
    }

    return Date.now() - lastRequest > settings.sessionLength;
  }

  /**
   * Requests push permission.
   * @returns {Promise}
   */
  pushRequest() {
    if (!window.appboy.isPushSupported()) {
      log.error('Push not supported.');
      return Promise.resolve();
    }

    if (window.appboy.isPushBlocked()) {
      log.debug('Push blocked.');
      return Promise.resolve();
    }

    if (window.appboy.isPushPermissionGranted()) {
      log.debug('Push already granted.');
      return Promise.resolve();
    }

    if (!this.canRequestPushPermission) {
      log.debug('Cannot request push permission yet.');
      return Promise.resolve();
    }

    return new Promise(resolve => {
      log.debug('Requesting Braze push permission.');

      store.set(this.lastPushRequestKey, Date.now());

      window.appboy.registerAppboyPushMessages(() => {
        log.debug('Braze push request granted.');
        resolve();
      }, () => {
        log.debug('Braze push request denied.');
        resolve();
      });
    });
  }

  /**
   * Set user last known location.
   * @param {Object} location - User location object.
   * @param {Object} neighborhoods - User neighborhoods object.
   * @returns {Promise}
   */
  setLastKnownLocation(location, neighborhoods) {
    if (location && location.length) {
      const { lat, lon } = location[0];
      const user = window.appboy.getUser();
      if (user) {
        user.setLastKnownLocation(lat, lon);
        log.debug('Braze set last known location.');
      }
    }

    if (neighborhoods && neighborhoods.length) {
      neighborhoods.forEach(neighborhood => {
        const eventName = `Neighborhood ${neighborhood.name}`;
        window.appboy.logCustomEvent(eventName);
        log.debug(`Braze custom event [${eventName}] triggered.`);
      });
    }

    return Promise.resolve();
  }
}

export default Braze;



// WEBPACK FOOTER //
// ./src/Braze.js